import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, DollarSign } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";

const fmt = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2 }).format(n || 0);

export default function RTOStockPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).some(r => ["Admin", "admin"].includes(r));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPriceDialog, setShowPriceDialog] = useState(false);
  const [priceItem, setPriceItem] = useState(null);
  const [priceValue, setPriceValue] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/rto-stock"); setItems(data); }
    catch (err) { console.error("API get /rto-stock error:", err); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setPrice = async () => {
    if (!priceItem) return;
    try {
      await api.put(`/rto-stock/${priceItem._id}/set-price`, { discounted_price: priceValue || null });
      toast.success("Price updated");
      setShowPriceDialog(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const expiryColor = (days) => {
    if (days === null || days === undefined) return "text-zinc-400";
    if (days > 60) return "text-green-600";
    if (days >= 30) return "text-amber-600";
    return "text-red-600 font-bold";
  };

  return (
    <div data-testid="rto-stock-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">RTO Stock</h1>
          <p className="text-sm text-zinc-500 mt-1">Good-condition returned stock available for sale</p>
        </div>
      </div>

      <div className="border border-zinc-200 overflow-x-auto" data-testid="rto-stock-table">
        <table className="data-table w-full">
          <thead><tr><th>SKU</th><th className="num">Qty (boxes)</th><th>MFD</th><th>Expiry</th><th className="num">Days to Expiry</th><th>Batch</th><th>Condition</th><th className="num">Disc. Price</th><th>Status</th><th>RTO Ref</th>{isAdmin && <th className="w-20">Actions</th>}</tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={11} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={11} className="text-center py-8 text-zinc-400 text-xs">No RTO stock available</td></tr> :
            items.map(item => (
              <tr key={item._id} data-testid={`rto-stock-${item._id}`}>
                <td className="text-xs font-medium">{item.sku_name}</td>
                <td className="num font-mono font-semibold">{item.quantity_boxes}</td>
                <td className="text-xs">{item.production_date ? fmtDate(item.production_date) : "—"}</td>
                <td className="text-xs">{item.expiry_date ? fmtDate(item.expiry_date) : "—"}</td>
                <td className={`num font-mono text-xs ${expiryColor(item.days_to_expiry)}`}>{item.days_to_expiry !== null ? item.days_to_expiry : "—"}</td>
                <td className="text-xs">{item.batch_number || "—"}</td>
                <td className="text-xs text-zinc-500">{item.condition_notes || "—"}</td>
                <td className="num font-mono text-xs">{item.discounted_price !== null ? `₹${fmt(item.discounted_price)}` : <span className="text-zinc-400 italic">Standard</span>}</td>
                <td><span className="px-1.5 py-0.5 text-[9px] font-semibold rounded-sm bg-green-100 text-green-700">{item.status}</span></td>
                <td className="text-xs font-mono">{item.rto_number}</td>
                {isAdmin && <td><Button variant="outline" className="rounded-sm h-6 text-[10px] px-2" onClick={() => { setPriceItem(item); setPriceValue(item.discounted_price || ""); setShowPriceDialog(true); }}><DollarSign size={10} /> Set Price</Button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Set Price Dialog */}
      <Dialog open={showPriceDialog} onOpenChange={setShowPriceDialog}>
        <DialogContent className="max-w-xs" data-testid="set-price-dialog">
          <DialogHeader><DialogTitle className="font-heading text-sm">Set Discounted Price</DialogTitle></DialogHeader>
          {priceItem && <p className="text-xs text-zinc-500">{priceItem.sku_name} — {priceItem.quantity_boxes} boxes</p>}
          <div><Label className="text-xs">Selling Price (INR) — leave blank for standard channel price</Label><Input type="number" step="0.01" value={priceValue} onChange={e => setPriceValue(e.target.value)} className="mt-1 rounded-sm text-xs" placeholder="e.g. 80.00" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriceDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={setPrice} className="rounded-sm text-xs">Save Price</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
